import { useState, useEffect, useCallback } from "react";
import { Card, btnPrimary, btnSecondary } from "./shared";

// ── Report definitions ────────────────────────────────────────────────────────
const BASIC_REPORTS = [
  { id: "rent_collection",     title: "Monthly Rent Collection",     desc: "All rent payments collected this month, grouped by tenant and property." },
  { id: "payment_history",     title: "Payment History by Tenant",   desc: "Full payment ledger sorted by tenant — paid, overdue, and pending." },
  { id: "expense_log",         title: "Basic Expense Log",           desc: "All recorded expenses with date, category, description, and amount." },
  { id: "outstanding_balance", title: "Outstanding Balance Report",  desc: "Tenants with unpaid or overdue rent and the amounts owed." },
];
const ADVANCED_REPORTS = [
  { id: "property_pl",           title: "Property P&L",                      desc: "Income vs. expenses per property for the current year." },
  { id: "year_over_year",        title: "Year-over-Year Income Comparison",   desc: "Monthly rent collected compared across years." },
  { id: "expense_by_category",   title: "Expense Breakdown by Category",      desc: "Total spend per expense category with percentage of total." },
  { id: "portfolio_performance", title: "Portfolio Performance Overview",     desc: "Net operating income, occupancy rate, and collection rate." },
  { id: "tax_summary",           title: "End-of-Year Tax Summary",            desc: "Annual income and deductible expenses formatted for tax prep." },
  { id: "vacancy_occupancy",     title: "Vacancy & Occupancy Rate Tracking",  desc: "Occupied vs. vacant units per property over the current period." },
];

// ── CSV helpers ───────────────────────────────────────────────────────────────
function buildCsvString(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(","),
    ...rows.map(r => headers.map(h => {
      const v = String(r[h] ?? "");
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(",")),
  ];
  return lines.join("\n");
}

// Reverses buildCsvString — a proper quote-aware parser (handles commas and
// doubled quotes inside quoted fields), not a naive split(","). buildCsvString
// now quotes on comma, double-quote, or newline (standard CSV quoting), so
// this round-trip is lossless.
function parseCsvString(csvString) {
  const rows = [];
  let field = "", row = [], inQuotes = false;
  for (let i = 0; i < csvString.length; i++) {
    const c = csvString[i];
    if (inQuotes) {
      if (c === '"') {
        if (csvString[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n") {
      row.push(field); rows.push(row); row = []; field = "";
    } else if (c !== "\r") {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  if (rows.length === 0) return [];
  const headers = rows[0];
  return rows.slice(1).filter(r => r.length > 1 || r[0] !== "").map(r => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = r[idx] ?? ""; });
    return obj;
  });
}

function downloadCSV(filename, rows) {
  const csvString = buildCsvString(rows);
  if (!csvString) return;
  const blob = new Blob([csvString], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function fmtMoney(n) { return "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 }); }
function fmtDate(d)  { return d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""; }

// Persists a copy to the "reports" bucket, logs it in report_history, and
// fires the report_ready notification — this is now the only thing that
// happens on generation, there's no automatic download anymore. Never
// surfaces errors to the user.
async function saveReportCopy(supabase, userId, actorId, reportType, title, rows) {
  if (!userId) return;
  const csvString = buildCsvString(rows);
  if (!csvString) return;
  try {
    const storagePath = `${userId}/${reportType}-${Date.now()}.csv`;
    const { error: uploadErr } = await supabase.storage
      .from("reports")
      .upload(storagePath, new Blob([csvString], { type: "text/csv" }), { contentType: "text/csv" });
    if (uploadErr) { console.error("[saveReportCopy] upload failed:", uploadErr.message); return; }
    const { error: insertErr } = await supabase
      .from("report_history")
      .insert([{ user_id: userId, report_type: reportType, title, storage_path: storagePath }]);
    if (insertErr) { console.error("[saveReportCopy] insert failed:", insertErr.message); return; }

    // message is just the title — AppShell renders the actor-aware sentence
    // ("You pulled..." / "[name] pulled...") from actor_id at display time.
    // link is the page name setPage() expects (this app has no URL router);
    // deep-linking into the history tab specifically is a later step.
    const { error: notifErr } = await supabase.from("notifications").insert([{
      user_id:  userId,
      actor_id: actorId,
      type:     "report_ready",
      message:  title,
      link:     "Reports",
      read:     false,
    }]);
    if (notifErr) console.error("[saveReportCopy] notification insert failed:", notifErr.message);
  } catch (err) {
    console.error("[saveReportCopy] unexpected error:", err.message);
  }
}

// ── CSV generators per report ─────────────────────────────────────────────────
function generateCSV(id, { payments, tenants, expenses, properties, units, selectedPeriod }) {
  const now      = new Date();
  const year     = selectedPeriod ? parseInt(selectedPeriod.split("-")[0]) : now.getFullYear();
  const month    = selectedPeriod ? selectedPeriod.split("-")[1] : String(now.getMonth() + 1).padStart(2, "0");
  const monthKey = selectedPeriod || `${year}-${month}`;

  switch (id) {
    case "rent_collection":
      return payments
        .filter(p => (p.due || p.paid || "").startsWith(monthKey))
        .map(p => ({ Tenant: p.tenant, Property: p.property || "", Amount: fmtMoney(p.amount), "Due Date": fmtDate(p.due), "Paid Date": fmtDate(p.paid), Status: p.status, Method: p.payment_method || "", "Invoice #": p.invoice_number || "" }));

    case "payment_history":
      return [...payments]
        .sort((a, b) => (b.due || "").localeCompare(a.due || ""))
        .map(p => ({ Tenant: p.tenant, Property: p.property || "", Amount: fmtMoney(p.amount), "Late Fee": fmtMoney(p.late_fee), "Due Date": fmtDate(p.due), "Paid Date": fmtDate(p.paid), Status: p.status, Method: p.payment_method || "", "Invoice #": p.invoice_number || "" }));

    case "expense_log":
      return [...expenses]
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
        .map(e => ({ Date: fmtDate(e.date), Category: e.category, Description: e.description, Amount: fmtMoney(e.amount) }));

    case "outstanding_balance":
      return tenants
        .filter(t => !t.is_complimentary && t.tenant_type !== "non_paying")
        .map(t => {
          const open = payments.filter(p => p.tenant === t.name && (p.status === "overdue" || p.status === "unpaid"));
          const balance = open.reduce((s, p) => s + (p.amount || 0) + (p.late_fee || 0), 0);
          return { Tenant: t.name, Property: t.address || "", Rent: fmtMoney(t.rent), "Outstanding Balance": fmtMoney(balance), "Open Records": open.length, Status: t.status };
        })
        .filter(r => r["Open Records"] > 0 || true);

    case "property_pl": {
      // unit_id -> units.property_id -> properties is the reliable path now
      // that every payment is backfilled; only fall back to the raw property
      // text (the old behavior) if unit_id is somehow missing.
      const resolveByUnit = (p) => {
        if (!p.unit_id) return null;
        const unit = (units || []).find(u => u.id === p.unit_id);
        const found = unit && (properties || []).find(pr => pr.id === unit.property_id);
        return found?.address || null;
      };
      const propMap = {};
      payments.filter(p => p.status === "paid" && (p.due || "").startsWith(String(year))).forEach(p => {
        const key = resolveByUnit(p) || p.property || "Unassigned";
        propMap[key] = (propMap[key] || 0) + (p.amount || 0);
      });
      const expMap = {};
      expenses.filter(e => (e.date || "").startsWith(String(year))).forEach(e => {
        const prop = properties.find(p => p.id === e.property_id);
        const key  = prop?.address || "Unassigned";
        expMap[key] = (expMap[key] || 0) + (e.amount || 0);
      });
      const keys = [...new Set([...Object.keys(propMap), ...Object.keys(expMap)])];
      return keys.map(k => ({ Property: k, Income: fmtMoney(propMap[k] || 0), Expenses: fmtMoney(expMap[k] || 0), "Net P&L": fmtMoney((propMap[k] || 0) - (expMap[k] || 0)), Year: year }));
    }

    case "year_over_year": {
      const byYear = {};
      payments.filter(p => p.status === "paid" && p.paid).forEach(p => {
        const y = p.paid.slice(0, 4);
        const m = p.paid.slice(0, 7);
        if (!byYear[y]) byYear[y] = {};
        byYear[y][m] = (byYear[y][m] || 0) + (p.amount || 0);
      });
      const rows = [];
      Object.keys(byYear).sort().forEach(y => {
        for (let mo = 1; mo <= 12; mo++) {
          const mk = `${y}-${String(mo).padStart(2, "0")}`;
          const label = new Date(mk + "-01").toLocaleString("en-US", { month: "long" });
          rows.push({ Year: y, Month: label, Income: fmtMoney(byYear[y][mk] || 0) });
        }
      });
      return rows;
    }

    case "expense_by_category": {
      const catMap = {};
      expenses.forEach(e => { catMap[e.category] = (catMap[e.category] || 0) + (e.amount || 0); });
      const total = Object.values(catMap).reduce((s, v) => s + v, 0);
      return Object.entries(catMap)
        .sort((a, b) => b[1] - a[1])
        .map(([cat, amt]) => ({ Category: cat, Total: fmtMoney(amt), "% of Expenses": total > 0 ? (amt / total * 100).toFixed(1) + "%" : "0%" }));
    }

    case "portfolio_performance": {
      const totalRent     = tenants.filter(t => !t.is_complimentary && t.tenant_type !== "non_paying").reduce((s, t) => s + (t.rent || 0), 0);
      const ytdCollected  = payments.filter(p => p.status === "paid" && (p.paid || "").startsWith(String(year))).reduce((s, p) => s + (p.amount || 0), 0);
      const ytdExpenses   = expenses.filter(e => (e.date || "").startsWith(String(year))).reduce((s, e) => s + (e.amount || 0), 0);
      const occupied      = tenants.filter(t => t.status === "active" || t.status === "month-to-month" || !t.lease_end).length;
      const totalUnits    = properties.reduce((s, p) => s + (p.units || 1), 0);
      return [{ Metric: "Monthly Expected Rent",     Value: fmtMoney(totalRent) },
              { Metric: "YTD Collected",              Value: fmtMoney(ytdCollected) },
              { Metric: "YTD Expenses",               Value: fmtMoney(ytdExpenses) },
              { Metric: "YTD Net Income",             Value: fmtMoney(ytdCollected - ytdExpenses) },
              { Metric: "Total Properties",           Value: properties.length },
              { Metric: "Total Units",                Value: totalUnits },
              { Metric: "Occupied Units",             Value: occupied },
              { Metric: "Occupancy Rate",             Value: totalUnits > 0 ? (occupied / totalUnits * 100).toFixed(1) + "%" : "0%" }];
    }

    case "tax_summary": {
      const yearStr = String(year);
      const SHARED_LABEL = "Shared / Multi-Property";

      // unit_id -> units.property_id -> properties is the reliable path now
      // that every payment is backfilled; only fall back to the old text
      // matching (raw property/tenant id against `properties`, then the
      // tenant's address on file) if unit_id is somehow missing.
      const resolvePaymentProperty = (p) => {
        if (p.unit_id) {
          const unit = (units || []).find(u => u.id === p.unit_id);
          const found = unit && (properties || []).find(pr => pr.id === unit.property_id);
          if (found) return found.address;
        }
        const str = String(p.property || "").trim();
        if (!str) {
          const t = tenants.find(t => t.name === p.tenant);
          return t?.address || t?.property || SHARED_LABEL;
        }
        const isRawId = /^\d+$/.test(str) ||
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
        if (isRawId) {
          const found = (properties || []).find(pr => String(pr.id) === str);
          if (found) return found.address;
          const t = tenants.find(t => t.name === p.tenant);
          return t?.address || t?.property || SHARED_LABEL;
        }
        return str;
      };

      // Same resolution as Financials.jsx's resolveExpProp, but the unlinked
      // fallback is relabeled — an expense with no property_id is a legitimately
      // unallocated shared bill (e.g. landscaping across the whole portfolio),
      // not missing data.
      const resolveExpenseProperty = (e) => {
        if (!e.property_id) return SHARED_LABEL;
        const found = (properties || []).find(pr => pr.id === e.property_id);
        return found?.address || SHARED_LABEL;
      };

      const yearPayments = payments.filter(p => p.status === "paid" && (p.due || "").startsWith(yearStr));
      const yearExpenses = expenses.filter(e => (e.date || "").startsWith(yearStr));

      // Every row shares this exact column set (blank where not applicable) so the
      // CSV/print output — which derives its header row from the first row's keys —
      // stays a single well-formed table across both sections.
      const row = (over = {}) => ({
        Section: "", Type: "", Category: "", Property: "", Tenant: "", Description: "",
        Due: "", Paid: "", Date: "", Amount: "", "Late Fee": "", "Payment Method": "",
        "Invoice #": "", "Receipt URL": "", "Tax Year": year, ...over,
      });

      const rows = [];

      // ── SECTION 1: SCHEDULE E-STYLE SUMMARY ─────────────────────────────
      rows.push(row({ Section: "SECTION 1: SUMMARY", "Tax Year": "" }));

      const rentByProp = {};
      let totalRent = 0;
      yearPayments.forEach(p => {
        const prop = resolvePaymentProperty(p);
        rentByProp[prop] = (rentByProp[prop] || 0) + (p.amount || 0);
        totalRent += (p.amount || 0);
      });
      rows.push(row({ Type: "INCOME", Category: "Rent Collected", Description: "Total rent collected, all properties", Amount: fmtMoney(totalRent) }));
      Object.entries(rentByProp).sort((a, b) => b[1] - a[1]).forEach(([prop, amt]) => {
        rows.push(row({ Type: "INCOME", Category: "Rent Collected", Property: prop, Description: `Rent — ${prop}`, Amount: fmtMoney(amt) }));
      });

      const catMap = {}; // { category: { property: amount } }
      let totalExpenses = 0;
      yearExpenses.forEach(e => {
        const cat  = e.category || "Uncategorized";
        const prop = resolveExpenseProperty(e);
        if (!catMap[cat]) catMap[cat] = {};
        catMap[cat][prop] = (catMap[cat][prop] || 0) + (e.amount || 0);
        totalExpenses += (e.amount || 0);
      });
      Object.entries(catMap)
        .sort((a, b) => Object.values(b[1]).reduce((s, v) => s + v, 0) - Object.values(a[1]).reduce((s, v) => s + v, 0))
        .forEach(([cat, byProp]) => {
          const catTotal = Object.values(byProp).reduce((s, v) => s + v, 0);
          rows.push(row({ Type: "EXPENSE", Category: cat, Description: `${cat} — all properties`, Amount: fmtMoney(catTotal) }));
          Object.entries(byProp).sort((a, b) => b[1] - a[1]).forEach(([prop, amt]) => {
            rows.push(row({ Type: "EXPENSE", Category: cat, Property: prop, Description: `${cat} — ${prop}`, Amount: fmtMoney(amt) }));
          });
        });

      rows.push(row({ Type: "NET", Category: "Net Income (before depreciation)", Amount: fmtMoney(totalRent - totalExpenses) }));
      rows.push(row({ Type: "NOTE", Description: "Leasly doesn't currently track depreciation, so this figure isn't Schedule E-final on its own." }));

      // ── SECTION 2: FULL TRANSACTION LEDGER ──────────────────────────────
      rows.push(row({ Section: "SECTION 2: TRANSACTION DETAIL", "Tax Year": "" }));

      [...yearPayments].sort((a, b) => (a.due || "").localeCompare(b.due || "")).forEach(p => {
        rows.push(row({
          Type: "Payment", Category: "Rent", Property: resolvePaymentProperty(p), Tenant: p.tenant || "",
          Description: `Rent — ${p.tenant || ""}`,
          Due: fmtDate(p.due), Paid: fmtDate(p.paid), Amount: fmtMoney(p.amount),
          "Late Fee": fmtMoney(p.late_fee), "Payment Method": p.payment_method || "", "Invoice #": p.invoice_number || "",
        }));
      });

      [...yearExpenses].sort((a, b) => (a.date || "").localeCompare(b.date || "")).forEach(e => {
        rows.push(row({
          Type: "Expense", Category: e.category || "", Property: resolveExpenseProperty(e),
          Description: e.description || "", Date: fmtDate(e.date), Amount: fmtMoney(e.amount),
          "Receipt URL": e.receipt_url || "",
        }));
      });

      return rows;
    }

    case "vacancy_occupancy":
      return properties.map(p => {
        const propTenants = tenants.filter(t => {
          const ta = (t.address || "").toLowerCase();
          const pa = (p.address || "").toLowerCase();
          return ta.includes(pa.slice(0, 10)) || pa.includes(ta.slice(0, 10));
        });
        const units    = p.units || 1;
        const occupied = propTenants.length;
        const vacant   = Math.max(0, units - occupied);
        return { Property: p.address, "Total Units": units, Occupied: occupied, Vacant: vacant, "Occupancy Rate": units > 0 ? (occupied / units * 100).toFixed(1) + "%" : "0%" };
      });

    default:
      return [];
  }
}

// ── Print helper — opens new tab, writes a styled HTML table, fires print dialog ──
function printReport(rows, title) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const win = window.open("", "_blank");
  if (!win) return;
  const bodyRows = rows.map(r =>
    `<tr>${headers.map(h => `<td>${String(r[h] ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;")}</td>`).join("")}</tr>`
  ).join("");
  win.document.write(`<!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <title>${title}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:28px;color:#2B2B2B}
      h1{font-size:20px;margin-bottom:4px;color:#163C2F}
      .sub{color:#9B9690;font-size:12px;margin-bottom:20px}
      table{border-collapse:collapse;width:100%}
      th{background:#163C2F;color:#fff;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
      td{padding:7px 12px;border-bottom:1px solid #E8E4DC}
      tr:nth-child(even) td{background:#F5F1EA}
      @media print{body{margin:0}}
    </style>
  </head><body>
    <h1>${title}</h1>
    <p class="sub">Generated ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</p>
    <table>
      <thead><tr>${headers.map(h=>`<th>${h}</th>`).join("")}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Reports({ session, payments, tenants, expenses, properties, units, supabase, setPage, selectedPeriod, landlordId }) {
  const tier     = session?.user?.user_metadata?.tier || "free";
  const isOwner  = tier === "owner";
  const isPro    = tier === "pro"  || isOwner;
  const isCore   = tier === "core" || tier === "starter" || isPro;
  const isFree   = !isCore;

  const [payModal,    setPayModal]    = useState(null);
  const [exporting,   setExporting]   = useState(null);
  const [loadingPay,  setLoadingPay]  = useState(false);
  const [printToast,  setPrintToast]  = useState(null); // { rows, title }
  const [reportHistory,        setReportHistory]        = useState([]);
  const [reportHistoryLoading, setReportHistoryLoading]  = useState(true);
  const [reportHistoryBusyId,  setReportHistoryBusyId]   = useState(null);

  const fetchReportHistory = useCallback(async () => {
    if (!landlordId) return;
    const { data, error } = await supabase
      .from("report_history")
      .select("*")
      .eq("user_id", landlordId)
      .order("created_at", { ascending: false });
    if (error) console.error("[Reports] report_history fetch failed:", error.message);
    setReportHistory(data || []);
    setReportHistoryLoading(false);
  }, [supabase, landlordId]);

  useEffect(() => { fetchReportHistory(); }, [fetchReportHistory]);

  const openReportHistoryFile = async (row, forceDownload) => {
    setReportHistoryBusyId(row.id);
    const { data, error } = await supabase.storage
      .from("reports")
      .createSignedUrl(row.storage_path, 60, forceDownload ? { download: true } : undefined);
    if (error || !data?.signedUrl) {
      setReportHistoryBusyId(null);
      console.error("[Reports] createSignedUrl failed:", error?.message);
      alert("Could not open this report. Please try again.");
      return;
    }
    if (forceDownload) {
      setReportHistoryBusyId(null);
      window.open(data.signedUrl, "_blank");
      return;
    }
    // "Open" renders the CSV as a viewable/printable page (same as the
    // existing print feature) instead of letting the browser download the
    // raw file, which is all a direct signed-URL open would otherwise do.
    try {
      const res = await fetch(data.signedUrl);
      const csvText = await res.text();
      const rows = parseCsvString(csvText);
      if (!rows.length) { alert("This report appears to be empty."); return; }
      printReport(rows, row.title);
    } catch (err) {
      console.error("[Reports] failed to fetch/parse report for viewing:", err.message);
      alert("Could not open this report. Please try again.");
    } finally {
      setReportHistoryBusyId(null);
    }
  };

  // Auto-dismiss toast after 8 s
  useEffect(() => {
    if (!printToast) return;
    const t = setTimeout(() => setPrintToast(null), 8000);
    return () => clearTimeout(t);
  }, [printToast]);

  // Capture the pending export on first render (before URL is cleaned)
  const [pendingExport] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const success    = params.get("export_success");
    const reportType = params.get("report_type");
    console.log("[Reports] mount — export_success:", success, "report_type:", reportType,
                "payments:", "?", "search:", window.location.search);
    if (success === "1" && reportType) {
      // Clean URL immediately so refresh doesn't re-trigger
      window.history.replaceState({}, "", window.location.pathname);
      return reportType;
    }
    return null;
  });

  // Fire the download once we know both the report type AND the data arrays are loaded.
  // Using payments.length as the proxy — empty on first render while loadAll() is in flight,
  // then populated on re-render, triggering this effect.
  useEffect(() => {
    if (!pendingExport) return;
    console.log("[Reports] pendingExport:", pendingExport,
                "payments:", payments.length, "tenants:", tenants.length,
                "expenses:", expenses.length, "properties:", properties.length);

    // Wait until at least one data array is populated (loadAll completed)
    const dataReady = payments.length > 0 || tenants.length > 0 ||
                      expenses.length > 0 || properties.length > 0;
    if (!dataReady) {
      console.log("[Reports] data not ready yet — waiting for re-render");
      return;
    }

    const rows = generateCSV(pendingExport, { payments, tenants, expenses, properties, units, selectedPeriod });
    console.log("[Reports] generateCSV rows:", rows.length, "for report:", pendingExport);

    if (rows.length) {
      const allReports = [...BASIC_REPORTS, ...ADVANCED_REPORTS];
      const report = allReports.find(r => r.id === pendingExport);
      const reportYear = selectedPeriod ? parseInt(selectedPeriod.split("-")[0]) : new Date().getFullYear();
      setPrintToast({ rows, title: report?.title || pendingExport });
      saveReportCopy(supabase, landlordId, session?.user?.id, pendingExport, `${report?.title || pendingExport} — ${reportYear}`, rows).then(fetchReportHistory);
    } else {
      console.warn("[Reports] generateCSV returned 0 rows — nothing to save");
    }

    logExport(pendingExport, 175);
  // Re-run whenever data arrays update so we catch the moment loadAll() populates them
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingExport, payments, tenants, expenses, properties, units, landlordId, fetchReportHistory]);

  const logExport = useCallback(async (reportType, amountPaid) => {
    await supabase.from("report_exports").insert([{
      user_id:     session?.user?.id,
      report_type: reportType,
      amount_paid: amountPaid,
    }]);
  }, [supabase, session]);

  const handleFreeExport = useCallback(async (report) => {
    setExporting(report.id);
    const rows = generateCSV(report.id, { payments, tenants, expenses, properties, units, selectedPeriod });
    if (rows.length) {
      setPrintToast({ rows, title: report.title });
      const reportYear = selectedPeriod ? parseInt(selectedPeriod.split("-")[0]) : new Date().getFullYear();
      saveReportCopy(supabase, landlordId, session?.user?.id, report.id, `${report.title} — ${reportYear}`, rows).then(fetchReportHistory);
    }
    await logExport(report.id, 0);
    setExporting(null);
  }, [payments, tenants, expenses, properties, units, logExport, selectedPeriod, supabase, landlordId, session, fetchReportHistory]);

  const handlePayAndDownload = useCallback(async () => {
    if (!payModal) return;
    setLoadingPay(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-report-payment", {
        body: { reportType: payModal.id },
      });
      if (error || !data?.url) {
        alert("Could not start payment. Please try again.");
        return;
      }
      window.location.href = data.url;
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setLoadingPay(false);
    }
  }, [payModal, supabase]);

  const ExportButton = ({ report, isAdvanced }) => {
    const isLocked = isAdvanced && isFree;
    if (isLocked) return null; // padlock handled by the card itself

    let cost = 0;
    if (isFree)       cost = 1.75; // free: always $1.75
    if (isCore && !isPro && isAdvanced) cost = 1.75; // core on advanced: $1.75
    // core on basic: free; pro/owner: always free

    const loading = exporting === report.id;
    return cost > 0
      ? <button onClick={() => setPayModal(report)} style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px", color: "#B08D57", borderColor: "#B08D57" }}>
          Export — $1.75
        </button>
      : <button disabled={loading} onClick={() => handleFreeExport(report)} style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px" }}>
          {loading ? "Exporting…" : "Export CSV"}
        </button>;
  };

  const rhThStyle = { padding: "10px 14px", textAlign: "left", fontSize: 10, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 };
  const rhTdStyle = { padding: "12px 14px", fontSize: 13, fontFamily: "'Manrope', sans-serif", color: "#2B2B2B" };

  return (
    <div>
      {/* ── Free plan upgrade banner ── */}
      {isFree && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFBF0", border: "1px solid #B08D5744", borderRadius: 10, padding: "12px 18px", marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: "#92400E", fontFamily: "'Manrope', sans-serif" }}>
            ✦ <strong>Upgrade to Core</strong> for unlimited free report exports on all basic reports.
          </div>
          <a href="/pricing" style={{ padding: "6px 16px", background: "#163C2F", color: "#F5F1EA", borderRadius: 999, textDecoration: "none", fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap", flexShrink: 0 }}>
            View Plans
          </a>
        </div>
      )}

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 32, color: "#2B2B2B", margin: 0 }}>Reports</h1>
        <div style={{ color: "#9B9690", marginTop: 4, fontSize: 14 }}>
          Export property and financial data as CSV.
          {isPro && " · Full access — all exports included."}
          {isCore && !isPro && " · Core plan — basic exports free, advanced exports $1.75 each."}
          {isFree && " · Free plan — exports $1.75 each, advanced reports require Core or Pro."}
        </div>
      </div>

      {/* ── Basic Reports ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Basic Reports</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {BASIC_REPORTS.map(report => (
            <Card key={report.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#2B2B2B", marginBottom: 4 }}>{report.title}</div>
                  <div style={{ fontSize: 12, color: "#9B9690", lineHeight: 1.5 }}>{report.desc}</div>
                </div>
                <ExportButton report={report} isAdvanced={false} />
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* ── Advanced Reports ── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
          Advanced Reports
          {isFree && <span style={{ marginLeft: 8, background: "#F5F1EA", border: "1px solid #E8E4DC", borderRadius: 4, padding: "2px 8px", fontSize: 10, color: "#9B9690" }}>Core & Pro only</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {ADVANCED_REPORTS.map(report => {
            const isLocked = isFree;
            return (
              <Card key={report.id} style={{ borderColor: isLocked ? "#E8E4DC" : undefined }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      {isLocked && <span style={{ fontSize: 14 }}>🔒</span>}
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#2B2B2B" }}>{report.title}</div>
                    </div>
                    <div style={{ fontSize: 12, color: "#9B9690", lineHeight: 1.5 }}>{report.desc}</div>
                    {isLocked && (
                      <div style={{ marginTop: 8, fontSize: 11, color: "#B08D57", fontWeight: 600 }}>
                        Available on Core and Pro plans —{" "}
                        <a href="/pricing" style={{ color: "#163C2F", textDecoration: "underline" }}>Upgrade</a>
                      </div>
                    )}
                  </div>
                  {!isLocked && <ExportButton report={report} isAdvanced={true} />}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── Report History ── */}
      <div style={{ marginTop: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Report History</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {reportHistoryLoading ? (
            <div style={{ padding: "32px 16px", textAlign: "center", color: "#9B9690", fontSize: 13 }}>Loading…</div>
          ) : reportHistory.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", color: "#9B9690", fontSize: 13 }}>No reports generated yet</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={rhThStyle}>Title</th>
                    <th style={rhThStyle}>Type</th>
                    <th style={rhThStyle}>Generated</th>
                    <th style={{ ...rhThStyle, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportHistory.map(row => {
                    const catalogEntry = [...BASIC_REPORTS, ...ADVANCED_REPORTS].find(r => r.id === row.report_type);
                    const busy = reportHistoryBusyId === row.id;
                    return (
                      <tr key={row.id} style={{ borderTop: "1px solid #F0EDE6" }}>
                        <td style={rhTdStyle}>{row.title}</td>
                        <td style={rhTdStyle}>{catalogEntry?.title || row.report_type}</td>
                        <td style={rhTdStyle}>{new Date(row.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                        <td style={{ ...rhTdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                          <button disabled={busy} onClick={() => openReportHistoryFile(row, false)} style={{ ...btnSecondary, fontSize: 12, padding: "5px 12px", marginRight: 8, opacity: busy ? 0.6 : 1 }}>Open</button>
                          <button disabled={busy} onClick={() => openReportHistoryFile(row, true)} style={{ ...btnPrimary, fontSize: 12, padding: "5px 12px", opacity: busy ? 0.6 : 1 }}>Download</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* ── Print toast ── */}
      {printToast && (
        <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 150, background: "#163C2F", color: "#F5F1EA", borderRadius: 14, padding: "18px 22px", boxShadow: "0 8px 40px rgba(0,0,0,0.28)", maxWidth: 320, fontFamily: "'Manrope', sans-serif", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>Report generated!</div>
            <div style={{ fontSize: 13, color: "rgba(245,241,234,0.7)", lineHeight: 1.5 }}>Would you like to print a copy?</div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => setPrintToast(null)}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid rgba(245,241,234,0.25)", background: "transparent", color: "rgba(245,241,234,0.55)", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600 }}
            >
              Dismiss
            </button>
            <button
              onClick={() => { printReport(printToast.rows, printToast.title); setPrintToast(null); }}
              style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: "#7FB98E", color: "#163C2F", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700 }}
            >
              Print Now
            </button>
          </div>
        </div>
      )}

      {/* ── Payment modal ── */}
      {payModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 16, padding: "32px 36px", maxWidth: 400, width: "100%", boxShadow: "0 12px 48px rgba(0,0,0,0.18)" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, color: "#2B2B2B", marginBottom: 6 }}>Export Report</div>
            <div style={{ fontSize: 13, color: "#9B9690", marginBottom: 20, lineHeight: 1.6 }}>{payModal.title}</div>

            <div style={{ background: "#F5F1EA", borderRadius: 10, padding: "16px 20px", marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, color: "#2B2B2B", fontWeight: 600 }}>One-time export fee</span>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: "#163C2F" }}>$1.75</span>
            </div>

            <p style={{ fontSize: 13, color: "#9B9690", marginBottom: 24, lineHeight: 1.6 }}>
              You'll be redirected to a secure Stripe checkout page. Your CSV download begins automatically on return.
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setPayModal(null)} style={btnSecondary}>Cancel</button>
              <button disabled={loadingPay} onClick={handlePayAndDownload} style={{ ...btnPrimary, opacity: loadingPay ? 0.6 : 1 }}>
                {loadingPay ? "Loading…" : "Pay & Download →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
